import { post } from "./client";
import { type Nominal, type OkResponse, type TypedUrl, url } from "./common";
import type { ProjectFormField } from "./spark";
import type { WorkshopId } from "./workshop";

// ---- IDs ----

export type FeedbackFormId = Nominal<string, "FeedbackForm">;

// ---- Sub-shapes ----

/**
 * When the form is offered. The API returns every pending form for the
 * workshop in one go; the host picks the one matching the trigger that fired.
 */
export type FeedbackTrigger =
  | "sparksCreation"
  | "afterRegistration"
  | "showroom"
  | "afterUpload";

/**
 * A feedback question. The same `formField` object Sanity uses for project
 * forms, with the types the compact renderer supports (validated on the
 * Sanity side, so the API never sends another one). Its `selection` needs the
 * `options` the project form has no use for.
 */
export type FeedbackFormField = Omit<ProjectFormField, "type"> & {
  type: "singleLine" | "multiLine" | "number" | "selection";
  options: string[] | null;
};

// ---- Response shapes ----

type FeedbackFormBase = {
  id: FeedbackFormId;
  trigger: FeedbackTrigger;
  title: string;
  message: string | null;
};

// Item in GET /workshops/:id/feedback-forms — a form this user hasn't answered,
// followed or dismissed yet, oldest first. A `form` asks its questions in the
// toast; a `link` shows a single button to an external page instead.
export type FeedbackFormDto =
  | (FeedbackFormBase & { mode: "form"; fields: FeedbackFormField[] })
  | (FeedbackFormBase & {
      mode: "link";
      buttonLabel: string;
      buttonLink: string;
    });

export type FeedbackAnswers = Record<string, string | number>;

// ---- Models ----

export const feedbackModels = {
  WORKSHOP_FEEDBACK_FORMS: (workshopId: WorkshopId) =>
    url<FeedbackFormDto[]>(`/workshops/${workshopId}/feedback-forms`),
};

// ---- Mutations ----

const feedbackFormsKey = (
  workshopId: WorkshopId,
): TypedUrl<FeedbackFormDto[]> =>
  feedbackModels.WORKSHOP_FEEDBACK_FORMS(workshopId);

export const feedbackMutations = {
  dismissFeedbackForm: ({
    workshopId,
    formId,
  }: {
    workshopId: WorkshopId;
    formId: FeedbackFormId;
  }) =>
    post<OkResponse>(
      `/workshops/${workshopId}/feedback-forms/${formId}/dismiss`,
      {},
      [feedbackFormsKey(workshopId)],
    ),
  clickFeedbackFormLink: ({
    workshopId,
    formId,
  }: {
    workshopId: WorkshopId;
    formId: FeedbackFormId;
  }) =>
    post<OkResponse>(
      `/workshops/${workshopId}/feedback-forms/${formId}/click`,
      {},
      [feedbackFormsKey(workshopId)],
    ),
  submitFeedbackForm: ({
    workshopId,
    formId,
    answers,
  }: {
    workshopId: WorkshopId;
    formId: FeedbackFormId;
    answers: FeedbackAnswers;
  }) =>
    post<OkResponse>(
      `/workshops/${workshopId}/feedback-forms/${formId}/submit`,
      { answers },
      [feedbackFormsKey(workshopId)],
    ),
};
